import QuestionEditor from '../../QuestionEditor'
import { snapshotable, serializable } from 'legend-builder/data/serialization'
import { cascadeFieldValidation } from 'legend-builder/mixins/validation'
import { observable, computed } from 'mobx'
import RynoEditor from 'legend-builder/ryno-editor/RynoEditor'
import { func, required } from 'validx'
import omit from 'lodash/omit'

/**
 * Prefills a personal tax organizer field through a prefill binding.
 * It has no ID, so nothing can reference it. Only relevant elements send
 * a prefill.
 */
@snapshotable
export default class PersonalOrganizerPrefillQuestionEditor extends QuestionEditor {
  static friendlyType = 'Organizer Prefill'
  static type = 'PersonalOrganizerPrefill'
  static viewOrder = 5
  static optionalId = true
  static isMetaElement = true
  static iconColor = '#0891B2'

  /**
   * The prefill binding. It decides the value type and which organizer
   * field gets prefilled.
   */
  @observable
  @serializable
  prefillBindingId = ''

  /**
   * The value formula.
   */
  @serializable
  expr

  rules = {
    id: [], // Removes the ID rules.
    prefillBindingId: [
      required('A prefill binding is required.'),
      func(
        () => !this.legendEditor.organizerBindings || !!this.binding,
        'Unknown prefill binding.'
      ),
    ],
    expr: [func((ctx) => ctx.value.analyze(), 'There are formula errors.')],
  }

  constructor(attrs, opts) {
    super(null, { ...opts, parse: false })
    this.expr = new RynoEditor({
      required: true,
      getSymbolTable: () => this.symbolTable,
      getNeededType: () => this.bindingValueType,
    })
    this.set(attrs, opts)
    cascadeFieldValidation(this, {
      prefillBindingId: ['expr'],
    })
  }

  /**
   * All prefill bindings in the catalog.
   */
  @computed
  get catalog() {
    return this.legendEditor.organizerBindings?.personal.prefill ?? []
  }

  /**
   * The selected binding, if it is in the catalog.
   */
  @computed
  get binding() {
    return this.catalog.find((b) => b.id === this.prefillBindingId) ?? null
  }

  /**
   * The label is computed from the binding, so it isn't edited or saved.
   */
  @computed
  get label() {
    const elementName = 'Personal Organizer Prefill'
    if (!this.binding) {
      return elementName
    }

    return `${this.binding.displayName} (${elementName})`
  }

  /**
   * The type the formula must return.
   * Not named `valueType`, because that would declare this element as a
   * symbol, and nothing can reference a prefill.
   */
  @computed
  get bindingValueType() {
    return this.binding?.valueType ?? null
  }

  /**
   * Bindings that can be selected. A binding can only be prefilled by one
   * element, so bindings used by other elements are left out.
   */
  @computed
  get bindingOptions() {
    const otherElements = this.legendEditor.questionsBuilder.allQuestions.filter(
      (q) => q.type === this.type && q !== this
    )
    const usedBindingIds = otherElements.map((q) => q.prefillBindingId)
    return this.catalog
      .filter((b) => !usedBindingIds.includes(b.id))
      .map((b) => [b.id, b.displayName])
  }

  /**
   * The symbol table to use for the Ryno Semantic Analyzer.
   */
  @computed
  get symbolTable() {
    return this.legendEditor.getSymbolTableFor(this)
  }

  parse({ expr, ...attrs }, opts) {
    this.expr.setValue(expr)
    // The label is computed from the binding, so a saved label is ignored.
    return super.parse(omit(attrs, 'label'), opts)
  }

  toJSON() {
    return {
      ...super.toJSON(...arguments),
      id: undefined, // Prefills don't have IDs.
    }
  }
}
